import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import { Card, CardBody, CardHeader, Spinner, Row, Col, Button } from "reactstrap";

/* ─── Helpers ─────────────────────────────────────────── */
const inputStyle = {
  height: 40,
  padding: "0 12px",
  fontSize: 14,
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111",
  width: "100%",
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 6,
  display: "block",
};

const SHIFT_COLORS = {
  1: "#4f86f7",
  2: "#f7a44f",
  3: "#52b788",
};

/* ─── Custom Tooltip ──────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #ddd",
      borderRadius: 8, padding: "10px 14px", fontSize: 13,
    }}>
      <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill, margin: "2px 0" }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────── */
const ProductionProReport = () => {
  const [machineId] = useState(() => localStorage.getItem("machineId") || 74);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  });

  const [data, setData] = useState([]);
  const [machineName, setMachineName] = useState("");
  const [loading, setLoading] = useState(false);

  /* ── Fetch ── */

   useEffect(() => {
      if (!machineId || !startDate || !endDate) return;{
        fetchData();
      }
    }, [machineId, startDate, endDate]);
  const fetchData = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.hanoch.in/api/mcc-excel-date-range/?machine_id=${machineId}&start_date=${startDate}&end_date=${endDate}`
      );
      const json = await res.json();
      setData(json.data || []);
      setMachineName(json.machine_name || "");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  /* ── Chart Data: group by date, stacked by shift ── */
  const chartData = (() => {
    const byDate = {};
    data.forEach((row) => {
      if (!byDate[row.date]) {
        byDate[row.date] = { date: row.date };
      }
      // breakdown_hours like "3:55:00" → decimal
      const [h, m] = row.breakdown_hours.split(":").map(Number);
      byDate[row.date][`Shift ${row.shift}`] = parseFloat((h + m / 60).toFixed(2));
      byDate[row.date][`goodParts_${row.shift}`] = row.good_part;
      byDate[row.date][`oee_${row.shift}`] = row.oee;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  /* ── OEE Chart Data ── */
  const oeeChartData = (() => {
    const byDate = {};
    data.forEach((row) => {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][`Shift ${row.shift}`] = parseFloat(row.oee?.replace("%", "") || 0);
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  /* ── Good Parts Chart Data ── */
  const goodPartsChartData = (() => {
    const byDate = {};
    data.forEach((row) => {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][`Shift ${row.shift}`] = row.good_part;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  /* ── Excel Export ── */
  const exportToExcel = () => {
    if (!data.length) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Overview (matching your image columns)
    const overviewRows = data.map((row) => ({
      Date: row.date,
      Shift: row.shift,
      "Part No": row.part_no,
      "Project Name": row.project_name,
      "Power Up Time": row.power_up_time,
      "Production Hrs": row.production_hours,
      "Breakdown Hours": row.breakdown_hours,
      "Good Part": row.good_part,
      "Bad Part": row.bad_part,
      "Avg Cycle Time": row.avg_cycle_time,
      OEE: row.oee,
      TEEP: row.teep,
    }));

    const ws = XLSX.utils.json_to_sheet(overviewRows, { origin: "B2" });

    // Column widths matching the image
    ws["!cols"] = [
      { wch: 4 },   // A (empty)
      { wch: 13 },  // Date
      { wch: 8 },   // Shift
      { wch: 10 },  // Part No
      { wch: 25 },  // Project Name
      { wch: 14 },  // Power Up Time
      { wch: 15 },  // Production Hrs
      { wch: 17 },  // Breakdown Hours
      { wch: 12 },  // Good Part
      { wch: 12 },  // Bad Part
      { wch: 16 },  // Avg Cycle Time
      { wch: 10 },  // OEE
      { wch: 10 },  // TEEP
    ];

   const sheetName = `${startDate.slice(5)}_${endDate.slice(5)}`;
XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${startDate}_shift_all.xlsx`);
  };

  const shifts = [1, 2, 3];

  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* ── Filter Row ── */}
        <Row className="align-items-end mb-4">
          <Col md={4}>
            <label style={labelStyle}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </Col>
          <Col md={4}>
            <label style={labelStyle}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </Col>
          <Col md={4}>
            <Button
              color="primary"
              onClick={fetchData}
              disabled={loading}
              style={{ height: 40, width: "100%" }}
            >
              {loading ? <Spinner size="sm" /> : "Show Report"}
            </Button>
          </Col>
          {/* {data.length > 0 && (
            <Col md={2}>
              <Button
                color="success"
                outline
                onClick={exportToExcel}
                style={{ height: 40, width: "100%" }}
              >
                Export Excel
              </Button>
            </Col>
          )} */}
        </Row>

        {data.length > 0 && (
          <>
            {/* ── Summary Cards ── */}
            {/* <Row className="mb-4">
              {[
                {
                  label: "Total Good Parts",
                  value: data.reduce((s, r) => s + r.good_part, 0).toLocaleString(),
                  icon: "ri-checkbox-circle-line",
                  color: "text-success",
                },
                {
                  label: "Total Bad Parts",
                  value: data.reduce((s, r) => s + r.bad_part, 0).toLocaleString(),
                  icon: "ri-close-circle-line",
                  color: "text-danger",
                },
                {
                  label: "Avg OEE",
                  value: (
                    data.reduce((s, r) => s + parseFloat(r.oee || 0), 0) / data.length
                  ).toFixed(1) + "%",
                  icon: "ri-bar-chart-line",
                  color: "text-primary",
                },
                {
                  label: "Total Shifts",
                  value: data.length,
                  icon: "ri-time-line",
                  color: "text-warning",
                },
              ].map((card, i) => (
                <Col md={3} key={i}>
                  <Card>
                    <CardBody className="d-flex align-items-center gap-3">
                      <div className={`fs-2 ${card.color}`}>
                        <i className={card.icon} />
                      </div>
                      <div>
                        <p className="mb-0 text-muted" style={{ fontSize: 12 }}>
                          {card.label}
                        </p>
                        <h5 className="mb-0">{card.value}</h5>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row> */}

            {/* ── Chart 1: Breakdown Hours ── */}
            {/* <Row className="mb-4">
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <h5 className="mb-0">Breakdown Hours by Date & Shift</h5>
                    <small className="text-muted">{machineName}</small>
                  </CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" />
                        <YAxis label={{ value: "Hours", angle: -90, position: "insideLeft" }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {shifts.map((s) => (
                          <Bar key={s} dataKey={`Shift ${s}`} fill={SHIFT_COLORS[s]} barSize={18} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              </Col>
            </Row> */}

            {/* ── Chart 2: Good Parts | OEE ── */}
            {/* <Row className="mb-4">
              <Col md={6}>
                <Card>
                  <CardHeader><h5 className="mb-0">Good Parts by Shift</h5></CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={goodPartsChartData} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {shifts.map((s) => (
                          <Bar key={s} dataKey={`Shift ${s}`} fill={SHIFT_COLORS[s]} barSize={14} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <CardHeader><h5 className="mb-0">OEE % by Shift</h5></CardHeader>
                  <CardBody>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={oeeChartData} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" />
                        <YAxis unit="%" domain={[0, 100]} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {shifts.map((s) => (
                          <Bar key={s} dataKey={`Shift ${s}`} fill={SHIFT_COLORS[s]} barSize={14} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </CardBody>
                </Card>
              </Col>
            </Row> */}

            {/* ── Data Table ── */}
            <Row>
              <Col md={12}>
                <Card>
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Report Data</h5>
                    <button className="btn btn-sm btn-outline-success" onClick={exportToExcel}>
                      Export Excel
                    </button>
                  </CardHeader>
                  <CardBody style={{ overflowX: "auto" }}>
                    <table className="table table-bordered table-sm" style={{ fontSize: 13 }}>
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Shift</th>
                          <th>Part No</th>
                          <th>Project Name</th>
                          <th>Power Up Time</th>
                          <th>Production Hrs</th>
                          <th>Breakdown Hours</th>
                          <th>Good Part</th>
                          <th>Bad Part</th>
                          <th>Avg Cycle Time</th>
                          <th>OEE</th>
                          <th>TEEP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((row, i) => (
                          <tr key={i}>
                            <td>{row.date}</td>
                            <td>{row.shift}</td>
                            <td>{row.part_no}</td>
                            <td>{row.project_name}</td>
                            <td>{row.power_up_time}</td>
                            <td>{row.production_hours}</td>
                            <td style={{ color: "#dc2626", fontWeight: 500 }}>
                              {row.breakdown_hours}
                            </td>
                            <td style={{ color: "#16a34a" }}>{row.good_part}</td>
                            <td style={{ color: "#dc2626" }}>{row.bad_part}</td>
                            <td>{row.avg_cycle_time}</td>
                            <td>{row.oee}</td>
                            <td>{row.teep}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductionProReport;